import { useState } from "react";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      setSubmitted(true);
    } catch (err) {
      // toast handled by sonner default
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="container-page pt-20 sm:pt-28 pb-12">
        <div className="max-w-3xl">
          <div className="overline">{t.contact.overline}</div>
          <h1 className="display-heading text-5xl sm:text-6xl mt-5 leading-[1.05]">{t.contact.title}</h1>
          <span className="accent-rule mt-8 block" />
          <p className="mt-8 text-lg leading-relaxed text-[hsl(var(--muted-foreground))]">{t.contact.lead}</p>
        </div>
      </section>

      <section className="container-page pb-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="border-l-2 border-[hsl(var(--accent))] pl-6">
            <div className="overline">{t.contact.seat}</div>
            <div className="mt-4 text-lg">Praça Damão, 7</div>
            <div className="text-lg">1400-085 Lisboa</div>
            <div className="text-lg">Portugal</div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div className="bg-white border border-[hsl(var(--primary))] p-10" data-testid="contact-success">
              <CheckCircle2 className="h-10 w-10 text-[hsl(var(--primary))]" />
              <h3 className="display-heading text-3xl mt-5 text-[hsl(var(--primary))]">
                {t.contact.successTitle}
              </h3>
              <p className="mt-3 text-[hsl(var(--muted-foreground))]">{t.contact.successBody}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <Row label={t.contact.fields.name} required>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="form-input"
                  data-testid="contact-name"
                />
              </Row>
              <Row label={t.contact.fields.email} required>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="form-input"
                  data-testid="contact-email"
                />
              </Row>
              <Row label={t.contact.fields.subject}>
                <input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="form-input"
                  data-testid="contact-subject"
                />
              </Row>
              <Row label={t.contact.fields.message} required>
                <textarea
                  rows={6}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="form-input resize-none"
                  data-testid="contact-message"
                />
              </Row>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary disabled:opacity-60"
                data-testid="contact-submit-button"
              >
                {loading ? t.common.sending : t.common.send}
              </button>
            </form>
          )}
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          background: white;
          border: 1px solid hsl(var(--border));
          padding: 10px 12px;
          font-family: "Outfit", sans-serif;
          font-size: 15px;
          color: hsl(var(--foreground));
        }
        .form-input:focus { outline: none; border-color: hsl(var(--accent)); }
      `}</style>
    </div>
  );
}

function Row({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">
        {label} {required && <span className="text-[hsl(var(--accent))]">*</span>}
      </label>
      {children}
    </div>
  );
}
